import type { ApprovalConditions } from '@clockwise/clients/approval-policies';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { CustomerAutocomplete } from '@/components/custom/customer-autocomplete';
import { ProductAutocomplete } from '@/components/custom/product-autocomplete';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { serviceRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

/**
 * Approval-policy condition editor — v1 scope.
 *
 * Four supported fields drive the timesheet context evaluation:
 *   - `customer_id`         (eq / neq)  → customer autocomplete
 *   - `product_id`          (eq / neq)  → product autocomplete (un-scoped)
 *   - `product_in_scope`    (eq)        → Yes / No toggle (boolean)
 *   - `owner_customer_role` (eq / neq)  → service-role dropdown; matches
 *       the row OWNER's per-customer service role on the entry's date.
 *       Powers trust-based shortcuts like the CFO auto-approve rule.
 *
 * One condition per policy in v1. The wire format is the framework's
 * full conditions shape: `{ logic: 'all', items: [<single item>] }` or
 * `null` (catch-all). Anything more exotic on the server side (multiple
 * items, unknown fields) shows up as a read-only "Complex condition"
 * placeholder so a save doesn't clobber it.
 */

export type EditorCondition =
  | { kind: 'none' }
  | { kind: 'customer'; op: 'eq' | 'neq'; customerId: string | null }
  | { kind: 'product'; op: 'eq' | 'neq'; productId: string | null }
  | { kind: 'product_in_scope'; value: boolean }
  | { kind: 'owner_customer_role'; op: 'eq' | 'neq'; serviceRoleId: string | null }
  | { kind: 'complex'; raw: ApprovalConditions };

interface Props {
  value: EditorCondition;
  onChange: (next: EditorCondition) => void;
  disabled?: boolean;
}

const FIELD_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'product', label: 'Activity' },
  { value: 'product_in_scope', label: 'Activity in scope' },
  { value: 'owner_customer_role', label: 'Owner role on customer' },
] as const;
type FieldOption = (typeof FIELD_OPTIONS)[number]['value'];

export function ApprovalConditionEditor({ value, onChange, disabled }: Props): React.ReactElement {
  if (value.kind === 'complex') {
    // Server has a conditions JSON shape the v1 editor doesn't speak.
    // Display read-only so save preserves the raw value (see
    // `toWireConditions` below).
    return (
      <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
        <div className="font-medium text-foreground">Complex condition</div>
        <div>
          This policy uses a condition shape the UI doesn't edit yet. Edit via the API or DB.
        </div>
      </div>
    );
  }

  if (value.kind === 'none') {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          No condition — this policy matches every submit. (Make it the OLDEST policy so newer
          specific-condition policies are evaluated first.)
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange({ kind: 'customer', op: 'eq', customerId: null })}
        >
          <Plus className="mr-1.5 size-3.5" /> Add condition
        </Button>
      </div>
    );
  }

  const fieldKey: FieldOption =
    value.kind === 'customer'
      ? 'customer'
      : value.kind === 'product'
        ? 'product'
        : value.kind === 'owner_customer_role'
          ? 'owner_customer_role'
          : 'product_in_scope';

  const switchField = (next: FieldOption) => {
    if (next === fieldKey) return;
    if (next === 'customer') onChange({ kind: 'customer', op: 'eq', customerId: null });
    else if (next === 'product') onChange({ kind: 'product', op: 'eq', productId: null });
    else if (next === 'owner_customer_role')
      onChange({ kind: 'owner_customer_role', op: 'eq', serviceRoleId: null });
    else onChange({ kind: 'product_in_scope', value: true });
  };

  return (
    <div className="space-y-3">
      {/* Field + Value carry the long-name selects (customer names,
          service-role names) — give them equal flexible space and pin
          Operator to a compact fixed width ("is / is not" never needs
          more than ~6rem). The remove button stays auto-width on the
          right. Previously Operator stole a full 1fr column, truncating
          long field labels like "Owner role on customer". */}
      <div className="grid grid-cols-[minmax(0,1fr)_6rem_minmax(0,1fr)_auto] gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Field</Label>
          <Select
            value={fieldKey}
            onValueChange={(v) => switchField(v as FieldOption)}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_OPTIONS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Operator</Label>
          {value.kind === 'product_in_scope' ? (
            <Select value="eq" disabled>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eq">is</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Select
              value={value.op}
              onValueChange={(v) =>
                onChange({ ...value, op: v as 'eq' | 'neq' } as EditorCondition)
              }
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eq">is</SelectItem>
                <SelectItem value="neq">is not</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Value</Label>
          {value.kind === 'customer' ? (
            <CustomerAutocomplete
              value={value.customerId}
              onChange={(c) => onChange({ ...value, customerId: c?.id ?? null })}
              disabled={disabled}
              placeholder="Pick a customer…"
            />
          ) : value.kind === 'product' ? (
            <ProductAutocomplete
              value={value.productId}
              onChange={(p) => onChange({ ...value, productId: p?.id ?? null })}
              disabled={disabled}
              placeholder="Pick an activity…"
            />
          ) : value.kind === 'owner_customer_role' ? (
            <ServiceRoleSelect
              value={value.serviceRoleId}
              onChange={(id) => onChange({ ...value, serviceRoleId: id })}
              disabled={disabled}
            />
          ) : (
            <Select
              value={value.value ? 'yes' : 'no'}
              onValueChange={(v) => onChange({ kind: 'product_in_scope', value: v === 'yes' })}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-end">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            onClick={() => onChange({ kind: 'none' })}
            aria-label="Remove condition"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Service-role dropdown. Pulls the tenant's ACTIVE service roles in a
 * single query and renders them sorted by name. We deliberately fetch
 * ALL active roles (not paginated) — a tenant typically has under 20.
 * Stores the role's UUID on save so renaming the role doesn't break
 * the policy.
 */
function ServiceRoleSelect({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}): React.ReactElement {
  const query = useQuery({
    queryKey: queryKeys.serviceRoles.list({
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 200,
    }),
    queryFn: () =>
      serviceRolesApi.list({ active: true, sortField: 'name', sortDirection: 'asc', limit: 200 }),
  });
  warnIfTruncated(query.data, 'approval-condition service-role picker');
  const roles = query.data?.data ?? [];
  return (
    <Select
      value={value ?? ''}
      onValueChange={(v) => onChange(v || null)}
      disabled={disabled || query.isLoading}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={query.isLoading ? 'Loading roles…' : 'Pick a service role…'} />
      </SelectTrigger>
      <SelectContent>
        {roles.map((r) => (
          <SelectItem key={r.id} value={r.id}>
            {r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Hydrate an `ApprovalConditions | null` from the server into the
 * editor's local state shape. Anything beyond a single-item condition
 * with a recognised field becomes `'complex'` (read-only, preserved on
 * save).
 */
export function conditionsFromServer(conditions: ApprovalConditions | null): EditorCondition {
  if (conditions == null) return { kind: 'none' };
  if (conditions.items.length !== 1) return { kind: 'complex', raw: conditions };
  const item = conditions.items[0];
  if (!item) return { kind: 'complex', raw: conditions };
  if (item.field === 'customer_id' && (item.op === 'eq' || item.op === 'neq')) {
    return {
      kind: 'customer',
      op: item.op,
      customerId: typeof item.value === 'string' ? item.value : null,
    };
  }
  if (item.field === 'product_id' && (item.op === 'eq' || item.op === 'neq')) {
    return {
      kind: 'product',
      op: item.op,
      productId: typeof item.value === 'string' ? item.value : null,
    };
  }
  if (item.field === 'product_in_scope' && item.op === 'eq') {
    return { kind: 'product_in_scope', value: Boolean(item.value) };
  }
  if (item.field === 'owner_customer_role' && (item.op === 'eq' || item.op === 'neq')) {
    return {
      kind: 'owner_customer_role',
      op: item.op,
      serviceRoleId: typeof item.value === 'string' ? item.value : null,
    };
  }
  return { kind: 'complex', raw: conditions };
}

/**
 * Serialize the editor's state back to the framework's `ApprovalConditions`
 * shape. Returns `null` for catch-all. Throws (via the returned `error`)
 * if a condition is selected but its required value isn't picked yet —
 * the caller surfaces this to the user before save.
 */
export function conditionsToServer(cond: EditorCondition): {
  conditions: ApprovalConditions | null;
  error: string | null;
} {
  if (cond.kind === 'none') return { conditions: null, error: null };
  if (cond.kind === 'complex') return { conditions: cond.raw, error: null };

  if (cond.kind === 'customer') {
    if (cond.customerId == null)
      return { conditions: null, error: 'Pick a customer for the condition.' };
    return {
      conditions: {
        logic: 'all',
        items: [{ field: 'customer_id', op: cond.op, value: cond.customerId }],
      },
      error: null,
    };
  }
  if (cond.kind === 'product') {
    if (cond.productId == null)
      return { conditions: null, error: 'Pick an activity for the condition.' };
    return {
      conditions: {
        logic: 'all',
        items: [{ field: 'product_id', op: cond.op, value: cond.productId }],
      },
      error: null,
    };
  }
  if (cond.kind === 'owner_customer_role') {
    if (cond.serviceRoleId == null)
      return { conditions: null, error: 'Pick a service role for the condition.' };
    return {
      conditions: {
        logic: 'all',
        items: [{ field: 'owner_customer_role', op: cond.op, value: cond.serviceRoleId }],
      },
      error: null,
    };
  }
  // product_in_scope
  return {
    conditions: {
      logic: 'all',
      items: [{ field: 'product_in_scope', op: 'eq', value: cond.value }],
    },
    error: null,
  };
}
